import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { IconCollection } from '../../components';
import css from './MultiSelectField.module.css';

const MultiSelectField = ({
  type = 'dropdown',
  label,
  subtitle,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Multi-select tool',
  maxSelections,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef(null);

  // Normalize selectedValues to handle both primitives and objects
  const normalizedValues = selectedValues.map(v => {
    if (typeof v === 'object' && v !== null) {
      return v.value ?? v.id ?? v;
    }
    return v;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionToggle = value => {
    const isSelected = normalizedValues.includes(value);
    let newValues;
    if (isSelected) {
      newValues = normalizedValues.filter(v => v !== value);
    } else {
      if (maxSelections && normalizedValues.length >= maxSelections) {
        newValues = [...normalizedValues.slice(1), value];
      } else {
        newValues = [...normalizedValues, value];
      }
    }
    onChange(newValues);
  };

  const handleRemove = value => {
    onChange(normalizedValues.filter(v => v !== value));
  };

  const getOptionLabel = value => {
    const opt = options.find(o => o.value === value);
    return opt ? opt.label : value;
  };

  // Open-fields mode: render all options as inline clickable chips
  if (type === 'openFields') {
    return (
      <div className={css.root}>
        {label && <p className={css.openFieldsLabel}>{label}</p>}
        {subtitle && <p className={css.openFieldsSubtitle}>{subtitle}</p>}
        <div className={css.openFieldsChips}>
          {options.map(option => {
            const isSelected = normalizedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={classNames(css.chip, { [css.chipSelected]: isSelected })}
                onClick={() => handleOptionToggle(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Default dropdown mode
  return (
    <div className={css.root}>
      {label && <label className={css.label}>{label}</label>}

      <div className={css.multiSelect} ref={dropdownRef}>
        <button type="button" className={css.trigger} onClick={() => setIsOpen(!isOpen)}>
          <span className={css.triggerText}>{placeholder}</span>
          <IconCollection name={isOpen ? 'arrowUp' : 'arrowDown'} />
        </button>

        {isOpen && (
          <div className={css.dropdown}>
            {options.map(option => {
              const isSelected = normalizedValues.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={classNames(css.option, { [css.optionSelected]: isSelected })}
                  onClick={e => {
                    e.preventDefault();
                    handleOptionToggle(option.value);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className={css.checkbox}
                  />
                  <span className={css.optionLabel}>{option.label}</span>
                </label>
              );
            })}
          </div>
        )}

        {normalizedValues.length > 0 && (
          <div className={css.pills}>
            {normalizedValues.map(value => (
              <span key={value} className={css.pill}>
                {getOptionLabel(value)}
                <button
                  type="button"
                  className={css.pillRemove}
                  onClick={() => handleRemove(value)}
                  aria-label="Remove"
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 0C2.23812 0 0 2.23875 0 5C0 7.76125 2.23812 10 5 10C7.76188 10 10 7.76125 10 5C10 2.23875 7.76188 0 5 0ZM7.31687 6.43313C7.43407 6.55032 7.49991 6.70926 7.49991 6.875C7.49991 7.04074 7.43407 7.19968 7.31687 7.31687C7.19968 7.43407 7.04074 7.49991 6.875 7.49991C6.70926 7.49991 6.55032 7.43407 6.43313 7.31687L5 5.88375L3.56687 7.31687C3.50897 7.37512 3.44012 7.42133 3.36428 7.45287C3.28845 7.48441 3.20713 7.50065 3.125 7.50065C3.04287 7.50065 2.96155 7.48441 2.88572 7.45287C2.80988 7.42133 2.74103 7.37512 2.68313 7.31687C2.56596 7.19967 2.50013 7.04073 2.50013 6.875C2.50013 6.70927 2.56596 6.55033 2.68313 6.43313L4.11625 5L2.68313 3.56687C2.56593 3.44968 2.50009 3.29074 2.50009 3.125C2.50009 2.95926 2.56593 2.80032 2.68313 2.68313C2.80032 2.56593 2.95926 2.50009 3.125 2.50009C3.29074 2.50009 3.44968 2.56593 3.56687 2.68313L5 4.11625L6.43313 2.68313C6.55032 2.56593 6.70926 2.50009 6.875 2.50009C7.04074 2.50009 7.19968 2.56593 7.31687 2.68313C7.43407 2.80032 7.49991 2.95926 7.49991 3.125C7.49991 3.29074 7.43407 3.44968 7.31687 3.56687L5.88375 5L7.31687 6.43313Z"
                      fill="#8F8C94"
                    />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectField;
